import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  AccountInfo,
  BrowserAuthError,
  AuthError as _AuthError,
} from '@azure/msal-browser';
// Re-export AuthError for use in other modules
export type AuthError = _AuthError;
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { msalConfig, loginRequest, BASE_SCOPES, allScopes, isAdminConsentRequired, extractErrorCode } from './msal-config';
import {
  initializeTeams,
  isInTeams,
  getTeamsContext,
  getTeamsSSOToken,
  registerThemeHandler,
  notifyAppLoaded,
  notifySuccess,
  getTeamsTheme,
} from './teams-auth';
import type { app as TeamsApp } from '@microsoft/teams-js';
import type { ConsentState, IncrementalConsentResult } from '@/lib/permissions/types';
import { DEFAULT_CONSENT_STATE } from '@/lib/permissions/types';

// Logger for auth flow - only log errors and critical events
const DEBUG_AUTH = process.env.NODE_ENV !== 'production';
const log = (area: string, message: string, data?: unknown) => {
  if (!DEBUG_AUTH) return;
  const timestamp = new Date().toISOString().split('T')[1];
  console.log(`[${timestamp}] 🔐 ${area}: ${message}`, data ?? '');
};

// Initialize MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize the MSAL instance
msalInstance.initialize().catch(console.error);

// Super admin domain - users from this domain have access to admin features
const SUPER_ADMIN_DOMAIN = '365apps.io';

/**
 * Extended auth context with consent management
 */
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  isInTeams: boolean;
  isSuperAdmin: boolean;
  teamsContext: TeamsApp.Context | null;
  user: AccountInfo | null;
  /** Current access token (null if not authenticated) */
  accessToken: string | null;
  /** Current consent state */
  consentState: ConsentState;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: (scopes?: string[]) => Promise<string | null>;
  /** Request additional scopes with admin consent detection */
  acquireAdditionalScopes: (scopes: string[]) => Promise<boolean>;
  /** Request incremental consent with detailed result */
  requestIncrementalConsent: (scopes: string[]) => Promise<IncrementalConsentResult>;
  /** Update consent state */
  updateConsentState: (state: Partial<ConsentState>) => void;
  /** Refresh consent state from current token */
  refreshConsentState: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Hook to access authentication context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Decode JWT to extract scopes
 */
function extractScopesFromToken(token: string): Set<string> {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return new Set();
    
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    const payload = JSON.parse(jsonPayload);
    const scp = payload.scp as string | undefined;
    
    return new Set(scp?.split(' ').filter(Boolean) || []);
  } catch {
    return new Set();
  }
}

/**
 * Determine consent state from token scopes
 */
function determineConsentStateFromToken(token: string): ConsentState {
  const scopes = extractScopesFromToken(token);
  const now = new Date().toISOString();
  
  // Check critical base scopes
  const criticalBaseScopes = ['User.Read', 'Files.Read'];
  const baseConsented = criticalBaseScopes.some(s => scopes.has(s));
  
  return {
    baseConsented,
    baseConsentedAt: baseConsented ? now : undefined,
    emailConsented: scopes.has('Mail.Read') || scopes.has('Mail.ReadBasic'),
    emailConsentedAt: scopes.has('Mail.Read') || scopes.has('Mail.ReadBasic') ? now : undefined,
    calendarConsented: scopes.has('Calendars.Read'),
    calendarConsentedAt: scopes.has('Calendars.Read') ? now : undefined,
    chatReadFallback: !scopes.has('ChatMessage.Read') && scopes.has('Chat.Read'),
    emailRequiresAdmin: false,
    calendarRequiresAdmin: false,
    lastCheckedAt: now,
  };
}

/**
 * Internal auth provider component that uses MSAL hooks
 */
function AuthProviderInner({ children }: { children: React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(true);
  const [inTeams, setInTeams] = useState(false);
  const [teamsContext, setTeamsContext] = useState<TeamsApp.Context | null>(null);
  const [teamsToken, setTeamsToken] = useState<string | null>(null);
  const [consentState, setConsentState] = useState<ConsentState>(DEFAULT_CONSENT_STATE);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  
  // Ref to track pending interactive token acquisition to prevent concurrent popups
  const pendingInteractiveRequest = useRef<Promise<string | null> | null>(null);

  const user = accounts[0] ?? null;
  
  log('AuthProvider', 'Render', { isAuthenticated, inProgress, hasUser: !!user });

  // Initialize Teams SDK if running in Teams
  useEffect(() => {
    let isMounted = true;

    async function init() {
      log('Init', 'Starting initialization...');
      const runningInTeams = isInTeams();
      
      if (!runningInTeams) {
        log('Init', 'Not running in Teams - skipping Teams SDK');
        if (isMounted) {
          setInTeams(false);
          setIsLoading(false);
        }
        return;
      }

      log('Init', 'Running in Teams - initializing SDK');
      setInTeams(true);
      
      const initialized = await initializeTeams();
      if (initialized && isMounted) {
        log('Init', 'Teams SDK initialized successfully');
        
        // Get Teams context
        const ctx = await getTeamsContext();
        if (isMounted) setTeamsContext(ctx);
        log('Init', 'Got Teams context', ctx?.user?.displayName);

        // Apply Teams theme
        const theme = await getTeamsTheme();
        applyTheme(theme);

        // Listen for theme changes
        registerThemeHandler((newTheme) => {
          applyTheme(newTheme as 'default' | 'dark' | 'contrast');
        });

        // Try Teams SSO
        const token = await getTeamsSSOToken();
        if (token && isMounted) {
          setTeamsToken(token);
          log('Init', 'Got Teams SSO token');
        }

        // Notify Teams we're ready
        notifyAppLoaded();
        notifySuccess();
      }

      if (isMounted) {
        log('Init', 'Initialization complete');
        setIsLoading(false);
      }
    }

    init();
    
    return () => { isMounted = false; };
  }, []);

  // Handle MSAL redirect response
  useEffect(() => {
    instance.handleRedirectPromise()
      .then(() => setIsLoading(false))
      .catch((error) => {
        console.error('Redirect error:', error);
        setIsLoading(false);
      });
  }, [instance]);

  useEffect(() => {
    if (inProgress === 'none') {
      setIsLoading(false);
    }
  }, [inProgress]);

  const login = useCallback(async () => {
    if (inTeams) {
      // In Teams, use Teams SSO
      const token = await getTeamsSSOToken();
      if (token) {
        setTeamsToken(token);
        return;
      }
    }

    // Use redirect flow (more reliable than popup)
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login redirect failed:', error);
    }
  }, [instance, inTeams]);

  const logout = useCallback(async () => {
    if (inTeams) {
      // In Teams, just clear local state - can't sign out of Teams
      setTeamsToken(null);
      return;
    }

    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      await instance.logoutRedirect();
    }
  }, [instance, inTeams]);

  const getAccessToken = useCallback(async (scopes: string[] = allScopes): Promise<string | null> => {
    log('Token', 'Requesting token', { scopes: scopes.join(', '), hasUser: !!user });
    
    if (!user && !teamsToken) {
      log('Token', 'No user or teams token - returning null');
      return null;
    }

    try {
      if (user) {
        log('Token', 'Attempting silent token acquisition...');
        const response = await instance.acquireTokenSilent({
          scopes,
          account: user,
        });
        log('Token', 'Silent acquisition successful');
        return response.accessToken;
      }
    } catch (error) {
      // Check if we need interactive auth
      if (error instanceof InteractionRequiredAuthError) {
        log('Token', 'Silent failed - interactive auth required');
        
        // Serialize interactive requests - reuse pending request if one exists
        if (pendingInteractiveRequest.current) {
          log('Token', 'Reusing pending interactive request');
          return pendingInteractiveRequest.current;
        }
        
        // Create new interactive request
        pendingInteractiveRequest.current = (async () => {
          try {
            log('Token', 'Starting interactive token acquisition (redirect)...');
            // Use redirect instead of popup for better reliability
            await instance.acquireTokenRedirect({ scopes });
            // This won't return - page will redirect
            return null;
          } catch (interactiveError) {
            // If redirect fails, try popup as fallback
            if (interactiveError instanceof BrowserAuthError && 
                (interactiveError as BrowserAuthError).errorCode === 'interaction_in_progress') {
              log('Token', 'Interaction already in progress - waiting...');
              // Wait a bit and retry silent
              await new Promise(resolve => setTimeout(resolve, 1000));
              try {
                const response = await instance.acquireTokenSilent({ scopes, account: user! });
                return response.accessToken;
              } catch {
                return null;
              }
            }
            console.error('Interactive token acquisition failed:', interactiveError);
            return null;
          } finally {
            pendingInteractiveRequest.current = null;
          }
        })();
        
        return pendingInteractiveRequest.current;
      }
      
      // Log other errors
      console.error('Token acquisition failed:', error);
    }
    
    return null;
  }, [instance, user, teamsToken]);

  /**
   * Request additional scopes - returns boolean for simple success/failure
   */
  const acquireAdditionalScopes = useCallback(async (scopes: string[]): Promise<boolean> => {
    if (!user) return false;

    try {
      const response = await instance.acquireTokenPopup({
        scopes,
        account: user,
      });
      
      // Update consent state based on new token
      if (response?.accessToken) {
        const newState = determineConsentStateFromToken(response.accessToken);
        setConsentState(prev => ({ ...prev, ...newState }));
      }
      
      return true;
    } catch (error) {
      console.error('Additional scope acquisition failed:', error);
      return false;
    }
  }, [instance, user]);

  /**
   * Request incremental consent with detailed result
   * Returns information about why consent failed (if it did)
   */
  const requestIncrementalConsent = useCallback(async (scopes: string[]): Promise<IncrementalConsentResult> => {
    if (!user) {
      return {
        success: false,
        error: {
          code: 'no_user',
          message: 'No user is signed in',
          requiresAdmin: false,
        },
      };
    }

    log('IncrementalConsent', 'Requesting scopes', scopes);

    try {
      // First try silent acquisition
      try {
        const silentResponse = await instance.acquireTokenSilent({
          scopes,
          account: user,
        });
        
        if (silentResponse?.accessToken) {
          const grantedScopes = extractScopesFromToken(silentResponse.accessToken);
          const allScopesGranted = scopes.every(s => grantedScopes.has(s));
          
          if (allScopesGranted) {
            log('IncrementalConsent', 'Silent acquisition successful');
            const newState = determineConsentStateFromToken(silentResponse.accessToken);
            setConsentState(prev => ({ ...prev, ...newState }));
            
            return {
              success: true,
              grantedScopes: Array.from(grantedScopes),
            };
          }
        }
      } catch (silentError) {
        log('IncrementalConsent', 'Silent acquisition failed, trying interactive');
      }

      // Try interactive consent
      const response = await instance.acquireTokenPopup({
        scopes,
        account: user,
      });

      if (response?.accessToken) {
        const grantedScopes = extractScopesFromToken(response.accessToken);
        log('IncrementalConsent', 'Interactive acquisition successful', Array.from(grantedScopes));
        
        const newState = determineConsentStateFromToken(response.accessToken);
        setConsentState(prev => ({ ...prev, ...newState }));
        
        return {
          success: true,
          grantedScopes: Array.from(grantedScopes),
        };
      }

      return {
        success: false,
        error: {
          code: 'no_token',
          message: 'No token received from consent flow',
          requiresAdmin: false,
        },
      };
    } catch (error) {
      const errorCode = extractErrorCode(error);
      const requiresAdmin = isAdminConsentRequired(error);
      
      log('IncrementalConsent', 'Failed', { errorCode, requiresAdmin, error });

      // Update consent state to mark admin requirement
      if (requiresAdmin) {
        const emailScope = scopes.includes('Mail.Read') || scopes.includes('Mail.ReadBasic');
        const calendarScope = scopes.includes('Calendars.Read');
        
        setConsentState(prev => ({
          ...prev,
          ...(emailScope ? { emailRequiresAdmin: true } : {}),
          ...(calendarScope ? { calendarRequiresAdmin: true } : {}),
          lastCheckedAt: new Date().toISOString(),
        }));
      }

      return {
        success: false,
        error: {
          code: errorCode,
          message: error instanceof Error ? error.message : 'Unknown error',
          requiresAdmin,
        },
      };
    }
  }, [instance, user]);

  /**
   * Update consent state
   */
  const updateConsentState = useCallback((state: Partial<ConsentState>) => {
    setConsentState(prev => ({ ...prev, ...state }));
  }, []);

  /**
   * Refresh consent state from current token
   */
  const refreshConsentState = useCallback(async () => {
    if (!user) return;

    try {
      const response = await instance.acquireTokenSilent({
        scopes: BASE_SCOPES,
        account: user,
      });

      if (response?.accessToken) {
        const newState = determineConsentStateFromToken(response.accessToken);
        setConsentState(newState);
        log('ConsentState', 'Refreshed', newState);
      }
    } catch (error) {
      log('ConsentState', 'Refresh failed', error);
    }
  }, [instance, user]);

  // In Teams, consider authenticated if we have Teams context (SSO)
  const effectivelyAuthenticated = isAuthenticated || (inTeams && !!teamsContext);
  
  // Determine the effective user
  const effectiveUser = user || (teamsContext ? createUserFromTeamsContext(teamsContext) : null);
  
  // Check if user is a super admin (from the vendor domain)
  const isSuperAdmin = (() => {
    const username = effectiveUser?.username?.toLowerCase() || '';
    return username.endsWith(`@${SUPER_ADMIN_DOMAIN}`);
  })();

  // Refresh consent state and access token when authentication changes
  useEffect(() => {
    if (effectivelyAuthenticated && user) {
      refreshConsentState();
      // Fetch initial access token
      getAccessToken().then(token => setAccessToken(token));
    } else {
      setAccessToken(null);
    }
  }, [effectivelyAuthenticated, user?.localAccountId, refreshConsentState, getAccessToken]);

  const value: AuthContextType = {
    isAuthenticated: effectivelyAuthenticated,
    isLoading,
    isInTeams: inTeams,
    isSuperAdmin,
    teamsContext,
    user: effectiveUser,
    accessToken,
    consentState,
    login,
    logout,
    getAccessToken,
    acquireAdditionalScopes,
    requestIncrementalConsent,
    updateConsentState,
    refreshConsentState,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Create a mock AccountInfo from Teams context for consistent API
 */
function createUserFromTeamsContext(ctx: TeamsApp.Context): AccountInfo {
  return {
    homeAccountId: ctx.user?.id || '',
    localAccountId: ctx.user?.id || '',
    environment: 'teams',
    tenantId: ctx.user?.tenant?.id || '',
    username: ctx.user?.userPrincipalName || '',
    name: ctx.user?.displayName,
  };
}

/**
 * Apply Teams theme to the document
 */
function applyTheme(theme: 'default' | 'dark' | 'contrast'): void {
  const root = document.documentElement;
  
  if (theme === 'dark' || theme === 'contrast') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  
  // Store theme preference
  localStorage.setItem('teams-theme', theme);
}

/**
 * Main auth provider component
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </MsalProvider>
  );
}
